import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  StopCircle, Volume2, Smartphone,
  PlayCircle, InfoIcon, AlertTriangleIcon, Loader2
} from 'lucide-react';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Modal from '../components/common/Modal';
import MobileSessionControls from '../components/session/MobileSessionControls';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { useAPIKey } from '../contexts/APIKeyContext';
import { useMediaDevices } from '../hooks/useMediaDevices';
import { useLocalStorage } from '../hooks/useLocalStorage';
import Footer from '../components/common/Footer';

const Session: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Debug logging
  console.log("Session page - user:", user, "authLoading:", authLoading);

  const { 
    isSessionActive, 
    sessionTime, 
    startSession, 
    stopSession, 
  } = useSession();
  const { apiKey } = useAPIKey();
  const mediaDevices = useMediaDevices();
  const [remainingMinutes, setRemainingMinutes] = useState(60); // Default value
  
  const [transcription, setTranscription] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [showOnboarding, setShowOnboarding] = useLocalStorage('hide-device-guidance', false);
  const [audioTestUrl, setAudioTestUrl] = useState<string | null>(null);

  // Handle authentication state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sign In Required</h2>
          <p className="text-gray-600 mb-6">
            You need to be signed in to access the session page.
          </p>
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Sign In
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/signup')}
              fullWidth
            >
              Create Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Format session time as MM:SS
  const formatSessionTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format remaining time as MM:SS
  const formatRemainingTime = (seconds: number) => {
    if (apiKey) return '∞'; // Show infinity symbol for API key
    
    const remainingSecs = remainingMinutes * 60 - seconds;
    if (remainingSecs <= 0) return '00:00';
    
    const mins = Math.floor(remainingSecs / 60);
    const secs = remainingSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-start session when component mounts if not active
  useEffect(() => {
    if (!isSessionActive) {
      if (!apiKey) {
        setShowWarning(true);
      }
    }
    
    return () => {
      if (isSessionActive) {
        stopSession();
        mediaDevices.stopMedia();
      }
    };
  }, []);

  // Mock transcription updates for demo
  useEffect(() => {
    if (isSessionActive && mediaDevices.micActive) {
      
      const messages = [
        "🎙️ Session started - I'm listening...",
        "📷 I can see your camera feed",
        "💬 How can I help you today?",
        "🔍 I'm ready to analyze what you show me",
        "✨ Ask me anything or show me something!"
      ];
      
      let messageIndex = 0;
      const interval = setInterval(() => {
        if (messageIndex < messages.length) {
          setTranscription(prev => [...prev, messages[messageIndex]]);
          messageIndex++;
          
          // Test audio playback with a simple TTS URL (mock)
          if (messageIndex === 2) {
            // Create a test audio URL for voice feedback testing
            const testMessage = "Hello! I can see your camera and hear your voice.";
            // Test audio with actual playback
            testAudioPlayback();
          }
        } else {
          clearInterval(interval);
        }
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [isSessionActive, mediaDevices.micActive]);

  const handleStartSession = () => {
    startSession();
    // Clear previous transcription
    setTranscription([]);
  };

  const handleStopSession = () => {
    stopSession();
    mediaDevices.stopMedia();
    setTranscription([]);
  };

  // Test audio playback
  const testAudioPlayback = async () => {
    try {
      await mediaDevices.initializeAudioContext();
      
      // Create a test beep sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      // Create beep sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Show success message
      setTranscription(prev => [...prev, "🔊 Audio test successful - you should hear a beep!"]);
      
    } catch (error) {
      console.error('Audio test failed:', error);
      setTranscription(prev => [...prev, "❌ Audio test failed - check volume and permissions"]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-4 sm:py-8"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Voice + Vision Session</h1>
            </div>
            <p className="text-gray-600">
              {isSessionActive 
                ? 'Your session is active - speak clearly and ensure good lighting'
                : 'Start your session to begin voice and vision interaction'
              }
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 flex items-center space-x-4">
            {apiKey && (
              <div className="text-center">
                <div className="text-sm text-gray-500">BYOK Mode</div>
                <div className="text-sm font-semibold text-green-600">
                  ∞ Unlimited
                </div>
              </div>
            )}
            
            <div className="text-center">
              <div className="text-sm text-gray-500">Session Time</div>
              <div className="text-xl font-mono font-semibold text-primary-700">
                {formatSessionTime(sessionTime)}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-gray-500">Remaining</div>
              <div className="text-xl font-mono font-semibold text-primary-700">
                {apiKey ? '∞' : formatRemainingTime(sessionTime)}
              </div>
            </div>
          </div>
        </div>

        {/* Warning for low time */}
        {showWarning && !apiKey && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-warning-50 border-l-4 border-warning-500 p-4 rounded-md"
          >
            <div className="flex">
              <AlertTriangleIcon className="h-6 w-6 text-warning-500 mr-3" />
              <div>
                <h3 className="font-medium text-warning-700">Session Time Notice</h3>
                <p className="text-warning-600">
                  {remainingMinutes <= 0
                    ? "You've used all your minutes for today. Please upgrade your plan or use your own API key."
                    : "You're using the free plan. Consider upgrading for more session time or use BYOK for unlimited access."
                  }
                </p>
                <div className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/subscription')}
                    className="mr-2 border-warning-500 text-warning-700 hover:bg-warning-50"
                  >
                    Upgrade Plan
                  </Button>
                  <button
                    onClick={() => setShowWarning(false)}
                    className="text-sm text-warning-600 hover:text-warning-800 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Session Interface */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Camera Feed */}
          <Card className="bg-white overflow-hidden">
            <div className="relative h-64 sm:h-80 bg-gray-900 flex items-center justify-center">
              {mediaDevices.isCameraLoading && (
                <div className="text-center text-white">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
                  <p className="font-medium">Loading camera...</p>
                  <p className="text-sm text-gray-300 mt-1">Please wait or enable permissions</p>
                </div>
              )}
              {mediaDevices.cameraActive && !mediaDevices.isCameraLoading ? (
                <video
                  ref={mediaDevices.videoRef}
                  autoPlay
                  muted
                  playsInline
                  controls={false}
                  width="100%"
                  height="100%"
                  style={{ transform: mediaDevices.facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                  className="w-full h-full object-cover bg-black"
                  onLoadedData={() => console.log('Session: Video loaded data')}
                  onCanPlay={() => console.log('Session: Video can play')}
                  onPlay={() => console.log('Session: Video playing')}
                  onError={(e) => console.error('Session: Video error', e)}
                />
              ) : !mediaDevices.isCameraLoading && (
                <div className="text-center text-white">
                  <div className="h-16 w-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">📷</span>
                  </div>
                  <p className="font-medium">Camera is off</p>
                  {mediaDevices.cameraError && (
                    <p className="text-red-400 text-sm mt-1">Permission required</p>
                  )}
                </div>
              )}
              
              {/* Camera status overlay */}
              {mediaDevices.cameraActive && (
                <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                  📹 {mediaDevices.facingMode === 'user' ? 'Front' : 'Rear'} Camera
                </div>
              )}
              
              {/* Debug info overlay */}
              {mediaDevices.cameraActive && (
                <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                  Stream: {mediaDevices.stream ? 'Active' : 'None'}
                </div>
              )}
            </div>
          </Card>

          {/* Transcription/Chat */}
          <Card className="bg-white overflow-hidden">
            <div className="h-64 sm:h-80 p-4 overflow-y-auto bg-gray-50">
              {transcription.length > 0 ? (
                <div className="space-y-3">
                  {transcription.map((text, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex"
                    >
                      <div className="bg-primary-100 text-primary-800 p-3 rounded-lg max-w-[80%]">
                        {text}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <Volume2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">
                      {isSessionActive
                        ? mediaDevices.micActive 
                          ? "🎙️ Listening... Speak clearly to see transcription."
                          : "Turn on your microphone to start interacting"
                        : "Start your session to begin voice and vision interaction"
                      }
                    </p>
                    {!isSessionActive && (
                      <p className="text-gray-400 text-sm mt-2">
                        Enable camera and microphone, then tap "Start Session"
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Mobile-Optimized Controls */}
        <div className="mt-6">
          <MobileSessionControls
            mediaState={mediaDevices}
            onStartCamera={() => mediaDevices.startCamera('user')}
            onStartMicrophone={mediaDevices.startMicrophone}
            onToggleCamera={mediaDevices.toggleCamera}
            onInitializeAudio={mediaDevices.initializeAudioContext}
            isSessionActive={isSessionActive}
          />
        </div>

        {/* Session Control Button */}
        <div className="mt-8 flex justify-center">
          {isSessionActive ? (
            <Button
              variant="danger"
              size="lg"
              onClick={handleStopSession}
              leftIcon={<StopCircle className="h-6 w-6" />}
              className="w-full sm:w-auto px-8"
            >
              End Session
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              onClick={handleStartSession}
              leftIcon={<PlayCircle className="h-6 w-6" />}
              className="w-full sm:w-auto px-8 bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600"
              disabled={(remainingMinutes <= 0 && !apiKey) || (!mediaDevices.cameraActive && !mediaDevices.micActive)}
            >
              Start Session
            </Button>
          )}
        </div>

        {/* Info Card */}
        <Card className="mt-8 p-4 border-none bg-primary-50">
          <div className="flex items-start">
            <InfoIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-primary-800">
              <p className="font-medium mb-1">📱 Mobile-Optimized Experience</p>
              <div className="space-y-2">
                <p>
                  • <strong>Camera:</strong> Tap "Turn On Camera" and allow permissions. Use "🔄 Flip" to switch between front/rear cameras.
                </p>
                <p>
                  • <strong>Microphone:</strong> Enable microphone for voice interaction and transcription.
                </p>
                <p>
                  • <strong>Audio:</strong> If voice feedback is blocked, tap "Enable Audio" to hear responses.
                </p>
                {apiKey ? (
                  <p>
                    • <strong>BYOK Mode:</strong> You have unlimited session time with your own API key.
                  </p>
                ) : (
                  <p>
                    • <strong>💡 Pro Tip:</strong> Enter your OpenAI API key on the home screen for unlimited sessions.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Device Guidance Modal */}
        <Modal
          isOpen={!showOnboarding}
          onClose={() => setShowOnboarding(true)}
          title="📱 Mobile Session Guide"
          showDontShowAgain
          onDontShowAgainChange={(checked) => {
            if (checked) {
              setShowOnboarding(true);
            }
          }}
        >
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Smartphone className="h-5 w-5 text-primary-600 mt-1" />
              <p>
                <strong>Mobile:</strong> Perfect for flexible camera movement and real-time object scanning. Use the flip camera feature to switch between front and rear cameras.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <Volume2 className="h-5 w-5 text-primary-600 mt-1" />
              <p>
                <strong>Audio:</strong> Ensure your device volume is up and tap "Enable Audio" if voice feedback doesn't work initially.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <InfoIcon className="h-5 w-5 text-primary-600 mt-1" />
              <p>
                <strong>Best Results:</strong> Ensure good lighting, speak clearly, and hold your device steady for optimal AI analysis.
              </p>
            </div>
          </div>
        </Modal>
        
        <Footer />
      </div>
    </motion.div>
  );
};

export default Session;